import ndcsMatch from './ndcsMatch';

const normalizeLot = (v) =>
  String(v ?? '')
    .trim()
    .toUpperCase();

export const budOf = (lot) =>
  lot?.expirationDate ? String(lot.expirationDate).substring(0, 10) : '';

const todayIso = () => new Date().toISOString().substring(0, 10);

/** What an inventory lot row at this pharmacy means for a scanned vial:
 *   - 'depleted'  remaining_qty <= 0 — an unverified-lot candidate (POM-6083);
 *   - 'expired'   stock on the shelf past its BUD — cannot be dispensed, and must
 *                 not be recorded by hand either (that would claim it unverified);
 *   - 'stocked'   dispensable stock — an ordinary vial.
 *  Mirrors listAvailableLotsForDispense's own predicate (remaining_qty > 0 AND
 *  expiration_date >= today) so a lot expiring today still counts as stocked. */
export const lotRowStatus = (row) => {
  if (Number(row?.remainingQty) <= 0) return 'depleted';
  const bud = budOf(row);
  if (bud && bud < todayIso()) return 'expired';
  return 'stocked';
};

/** Resolve a scanned lot number to THE inventory row it names at this pharmacy
 *  for the Rx's product, so the caller can act on the row's status (see
 *  lotRowStatus) straight from the scan — add it as an ordinary vial, add it
 *  as an unverified lot, or refuse it — without the manual-entry dialog.
 *
 *  `rows` is a set of inventory lot rows (e.g. the inventoryLotsByLotId result
 *  for the read lot number: every pharmacy, every product). The match is
 *  deliberately strict; anything short of it falls back to the dialog, where
 *  the operator decides:
 *   - the row must be THIS pharmacy's, and
 *   - it must belong to the Rx's prescribed product — a lot number that
 *     collides with another product's is never added on the strength of a
 *     scan, and
 *   - if the read carried an NDC it must agree with the row's — a disagreement
 *     means either the read or the row is wrong, and
 *   - exactly ONE row must remain — the same lot number under several BUDs is
 *     legitimate (a lot repeats across shipments) but cannot be guessed, and
 *   - that row must carry a BUD — a BUD-less lot needs the operator to read the
 *     date off the vial, which is what the dialog asks for.
 *
 *  Returns the matched row, or null. */
const matchScannedLotToInventory = ({ rows, lotNumber, fulfillingPharmacyId, productId, ndc }) => {
  const lot = normalizeLot(lotNumber);
  if (!lot || !fulfillingPharmacyId || !productId) return null;
  const candidates = (rows ?? []).filter(
    (row) =>
      normalizeLot(row?.lotNumber) === lot &&
      row.fulfillingPharmacyId === fulfillingPharmacyId &&
      row.productId === productId &&
      (!ndc || ndcsMatch(row.ndc, ndc))
  );
  if (candidates.length !== 1) return null;
  const [match] = candidates;
  return match.expirationDate ? match : null;
};

export default matchScannedLotToInventory;
